import React, { useState, useEffect, useMemo, useRef } from 'react'
import { toUnicode } from 'punycode'
import * as lz from 'lz-string'
import SVG from 'react-inlinesvg'
import { useDrag, useDrop, useDragLayer } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import ReactTooltip from 'react-tooltip'
import {
  columnsDesc,
  groupFilterKeys,
  groupLabels,
  initialPage,
  perPage,
  tableColumnsKey,
  tableLabels,
} from './constants'
import { useStatistics } from './context'
import {
  TableEmpty,
  useWindowSize,
  useIsOverflowX,
  useTableEmpty,
  calculateValues,
  navigateTo,
  varClass,
  isBlank,
  map,
  copyToClipboard,
  useSnackbar,
  truncate,
} from '../../shared'
import { getRequest, postRequest, deleteRequest } from '../../../services'
import {
  findFavorite,
  statisticsCustomFilter,
  statisticsPaginate,
  statisticsSort,
  sumRows,
  groupStatisticsRows,
  enrichStatisticsRows,
} from './helpers'
import { TableValue } from './TableValue'

export const Table = (props) => {
  const {
    sort,
    dir,
    prevSort,
    prevDir,
    toggleSort,
    isSingleDate,
    unknownRow,
  } = props
  const {
    data,
    prevData,
    setTotalCount,
    networks,
    assignees,
    statuses,
    adsStats,
    group,
    search,
    filters,
    setFilters,
    favorites,
    setFavorites,
    refreshFavorites,
    columns,
    setColumns,
    availableColumns,
    crFilter,
    ecrFilter,
    favFilter,
    datePickerState,
    currencyCode,
  } = useStatistics()

  const [draggedColumn, setDraggedColumn] = useState('')

  const swapColumns = (value1: string, value2: string) => {
    const index1 = columns.indexOf(value1)
    const index2 = columns.indexOf(value2) || 1
    if (index1 === index2) return
    if (index1 === 0 || index2 === 0) return
    const newColumns = [...columns]
    const item = newColumns[index1]
    newColumns.splice(index1, 1)
    newColumns.splice(index2, 0, item)
    setColumns(newColumns)
    localStorage.setItem(tableColumnsKey(group), newColumns.join(','))
  }

  const shownColumns = columns.filter((c) => availableColumns.includes(c))

  const [isEmpty, setIsEmpty] = useTableEmpty(data.rows)

  const dataRows = useMemo(() => {
    const rows = groupStatisticsRows({
      data,
      prevData,
      group,
      datePickerState,
    })
    return enrichStatisticsRows({
      rows,
      group,
      adsStats,
    })
  }, [data.rows, prevData?.rows, group, adsStats])
  const filteredRows = useMemo(
    () =>
      statisticsCustomFilter({
        data: dataRows,
        search,
        filters,
        favorites,
        assignees,
        statuses,
        group,
        crFilter,
        ecrFilter,
        favFilter,
      }),
    [
      dataRows,
      search,
      filters,
      favorites,
      assignees,
      statuses,
      group,
      crFilter,
      ecrFilter,
      favFilter,
    ]
  )
  const sortedRows = useMemo(
    () =>
      statisticsSort(filteredRows, [
        { sort: prevSort, dir: prevDir },
        { sort, dir },
      ]),
    [filteredRows, sort, dir, prevSort, prevDir]
  )

  useEffect(() => {
    setTotalCount(sortedRows?.length ?? 0)
  }, [sortedRows])

  const [page, setPage] = useState(0)
  const hasMore = initialPage + page * perPage < filteredRows.length
  const loadMore = () => setPage(page + 1)
  const loadMoreScroll = (event) => {
    if (hasMore) {
      const element = event.target
      const bottom = element.scrollHeight - element.offsetHeight - 100
      const isScrolledToBottom = element.scrollTop >= bottom
      if (isScrolledToBottom) {
        loadMore()
      }
    }
  }
  useEffect(() => {
    if (page > 0) {
      setPage(0)
    }
    const table = document.querySelector('.insights-statistics-table')
    if (table) {
      const element = table.parentElement
      element.scrollTo(element.scrollLeft, 0)
    }
  }, [data.rows, search, group, sort, dir])

  const shownRows = statisticsPaginate(sortedRows, initialPage + page * perPage)

  const ref = useRef<HTMLDivElement>()
  const [overflowRows, setOverflowRows] = useState(0)
  useEffect(() => {
    if (shownRows.length) {
      if (ref.current) {
        const availableHeight = ref.current.offsetHeight - shownRows.length * 64
        const newOverflowRows = Math.floor(availableHeight / 64) - 1
        if (overflowRows !== newOverflowRows) {
          setOverflowRows(newOverflowRows)
        }
      }
    } else {
      setOverflowRows(0)
    }
  }, [shownRows, group])

  const windowSize = useWindowSize()
  const isOverflowX = useIsOverflowX(ref, undefined, [
    group,
    search,
    columns,
    shownRows,
    windowSize,
  ])

  const totalValues = useMemo(() => {
    if (search) {
      return calculateValues(sumRows([...filteredRows]), currencyCode)
    } else {
      return calculateValues(
        sumRows([...filteredRows, unknownRow]),
        currencyCode
      )
    }
  }, [search, filteredRows, unknownRow, currencyCode])
  // const totalValues = calculateValues(data.totals)

  return (
    <>
      <div className="insights-statistics-table-container">
        <div
          className="table-container-overflow"
          ref={ref}
          onScroll={loadMoreScroll}
        >
          <table className="table table-bordered table-sortable table-hoverable insights-statistics-table">
            <thead className="text-nowrap">
              <tr>
                {shownColumns.map((column, index) => {
                  return (
                    <TableHead
                      key={column}
                      index={index}
                      value={column}
                      label={tableLabels[column] || groupLabels[group]}
                      desc={columnsDesc.includes(column)}
                      {...{
                        toggleSort,
                        sort,
                        dir,
                        columns,
                        draggedColumn,
                        setDraggedColumn,
                        swapColumns,
                      }}
                    />
                  )
                })}
              </tr>
            </thead>
            <tbody className="text-nowrap">
              {map(shownRows, (row, index) => {
                if (
                  (!row.name && !row.grp) ||
                  (row.name && row.name.toLowerCase() === 'unknown')
                ) {
                  return null
                }
                return (
                  <TableItem
                    key={row.grp || row.name || index}
                    {...{
                      row,
                      networks,
                      assignees,
                      statuses,
                      adsStats,
                      sort,
                      dir,
                      group,
                      filters,
                      setFilters,
                      columns: shownColumns,
                      draggedColumn,
                      favorites,
                      setFavorites,
                      refreshFavorites,
                      datePickerState,
                      currencyCode,
                      isSingleDate,
                    }}
                  />
                )
              })}
              {!isEmpty && isBlank(shownRows) && (
                <tr>
                  <td
                    colSpan={shownColumns?.length ?? 10}
                    className="text-center text-light text-bolder"
                  >
                    <div className="max-w-100vw">Nothing found</div>
                  </td>
                </tr>
              )}
              {overflowRows > 0 &&
                map(overflowRows, (_, i) => (
                  <tr key={`overflow-${i}`} className="blank-row">
                    <td colSpan={10} />
                  </tr>
                ))}
            </tbody>
            {!isBlank(data.totals) && (
              <tfoot
                className={varClass({
                  'no-overflow': !isOverflowX,
                })}
              >
                {unknownRow && !search ? (
                  <TableItem
                    {...{
                      row: unknownRow,
                      sort: null,
                      dir,
                      group,
                      filters,
                      setFilters,
                      columns: shownColumns,
                      draggedColumn,
                      favorites,
                      setFavorites,
                      refreshFavorites,
                      datePickerState,
                      currencyCode,
                      isSingleDate,
                      hideFilter: true,
                      tooltipLabel: (
                        <>
                          <div style={{ textAlign: 'left' }}>
                            <p style={{ fontWeight: 400 }}>
                              Conversion details could be missing if:
                            </p>
                            <ul
                              style={{
                                listStyle: 'none',
                                padding: 0,
                                fontStyle: 'italic',
                                fontWeight: 400,
                              }}
                            >
                              <li>- Clicks are not sent through Heylink.</li>
                              <li>
                                - The Heylink script is not installed correctly.
                              </li>
                              <li>
                                - Historic conversion data from before you
                                actived Heylink was imported from your networks.
                              </li>
                            </ul>
                          </div>
                        </>
                      ),
                      isUnknownRow: true,
                    }}
                  />
                ) : null}
                <tr className="text-nowrap">
                  <th className="text-dark text-bolder background-new-gray-light sticky left-0 z-2">
                    <div className="first-column">Total</div>
                  </th>
                  {shownColumns.map((column, index) =>
                    index === 0 ? null : (
                      <th
                        key={column}
                        className={varClass({
                          'text-right background-new-gray-light': true,
                          'drag-highlight': draggedColumn === column,
                        })}
                      >
                        <TableValue values={totalValues} column={column} />
                      </th>
                    )
                  )}
                </tr>
              </tfoot>
            )}
          </table>
          <ReactTooltip
            id="table-tooltip"
            type="dark"
            effect="solid"
            place="bottom"
          />
          <DragTable
            {...{
              data,
              shownRows,
              group,
              columns: shownColumns,
              filters,
              datePickerState,
              currencyCode,
            }}
          />
        </div>
      </div>

      {isEmpty && (
        <TableEmpty
          icon="insights"
          title="No data available yet"
          subtitle="To start using Reports simply connect a network"
          buttons={{
            '/networks': 'Connect network',
          }}
          setIsEmpty={setIsEmpty}
        />
      )}
    </>
  )
}

const TableHead = (props) => {
  const {
    value,
    label,
    desc,
    sort,
    dir,
    toggleSort,
    index,
    columns,
    draggedColumn,
    setDraggedColumn,
    swapColumns,
    hideSort,
  } = props

  const ref = useRef(null)

  const [{ isDragging }, dragHandle, drag] = useDrag(() => ({
    type: 'column',
    item: () => {
      setDraggedColumn(value)
      return { value }
    },
    end: (item) => {
      setDraggedColumn('')
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }))

  const [_props, drop] = useDrop({
    accept: 'column',
    hover: (item, monitor) => {
      if (!ref.current) {
        return
      }
      const value1 = item.value
      const value2 = value
      if (value1 === value2) {
        return
      }
      const index1 = columns.indexOf(value1)
      const index2 = columns.indexOf(value2)

      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      const hoverMiddleX =
        (hoverBoundingRect.right - hoverBoundingRect.left) / 2
      const hoverQuarterX =
        (hoverBoundingRect.right - hoverBoundingRect.left) / 4

      const clientOffset = monitor.getClientOffset()
      const hoverClientX = clientOffset.x - hoverBoundingRect.left

      if (index1 < index2 && hoverClientX < hoverMiddleX - hoverQuarterX) {
        return
      }
      if (index1 > index2 && hoverClientX > hoverMiddleX + hoverQuarterX) {
        return
      }

      swapColumns(value1, value2)
    },
    drop: (item: any) => {
      swapColumns(item.value, value)
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  })

  drag(getEmptyImage())
  drop(ref)

  if (index === 0) {
    return (
      <>
        <th
          onClick={() => {
            if (!hideSort) {
              toggleSort(value, desc ? 'desc' : 'asc').call(this)
            }
          }}
          ref={ref}
          className={varClass({
            'vertical-middle sticky left-0 z-2': true,
            'sort-highlight': sort === value,
          })}
        >
          <div className="first-column p-r-4">
            {draggedColumn && (
              <div
                className={varClass({
                  'drop-space': true,
                  'opaque': draggedColumn === columns[index],
                })}
              />
            )}
            <div className="row row-fill row-nowrap">
              <span
                className={varClass({
                  'column-label': true,
                  'cursor-default': hideSort,
                })}
              >
                {label}
                {!hideSort && (
                  <>
                    {sort === value ? (
                      <>
                        {dir === 'desc' ? (
                          <SVG
                            src="/images/insights/caret-down.svg"
                            className="m-l-1"
                          />
                        ) : (
                          <SVG
                            src="/images/insights/caret-up.svg"
                            className="m-l-1"
                          />
                        )}
                      </>
                    ) : (
                      <SVG src="/images/insights/caret.svg" className="m-l-1" />
                    )}
                  </>
                )}
              </span>
            </div>
          </div>
        </th>
      </>
    )
  } else {
    return (
      <th
        onClick={() => {
          if (!hideSort) {
            toggleSort(value, desc ? 'desc' : 'asc').call(this)
          }
        }}
        ref={ref}
        className={varClass({
          'relative vertical-middle p-l-2 p-r-4': true,
          'sort-highlight': sort === value,
          'drag-highlight': draggedColumn === value,
        })}
        style={{
          color: isDragging ? 'transparent' : '',
        }}
      >
        {draggedColumn && <div className="drop-space" />}
        <div className="row row-fill row-nowrap">
          <span ref={dragHandle} className="cursor-grab m-r-10">
            <SVG src="/images/insights/icon-drag.svg" className="handle" />
          </span>
          <span
            className={varClass({
              'column-label': true,
              'cursor-default': hideSort,
            })}
          >
            {label}
            {label === 'Pending commission' && (
              <>
                <span
                  data-tip="Part of total commission that's still pending approval"
                  data-for="pend-tooltip"
                  className="m-x-1"
                >
                  <SVG src="/images/recommendations/icon-info.svg" />
                </span>
                <ReactTooltip
                  id="pend-tooltip"
                  type="dark"
                  effect="solid"
                  place="right"
                />
              </>
            )}
            {label === 'Boost added value' && (
              <>
                <span
                  data-tip="Commission added by campaign boost & approx. total commission without boost"
                  data-for="boost-tooltip"
                  className="m-x-1"
                >
                  <SVG src="/images/recommendations/icon-info.svg" />
                </span>
                <ReactTooltip
                  id="boost-tooltip"
                  type="dark"
                  effect="solid"
                  place="right"
                />
              </>
            )}
            {label === 'ECR' && (
              <>
                <span
                  data-tip="Effective Commission Rate"
                  data-for="ecr-tooltip"
                  className="m-x-1"
                >
                  <SVG src="/images/recommendations/icon-info.svg" />
                </span>
                <ReactTooltip
                  id="ecr-tooltip"
                  type="dark"
                  effect="solid"
                  place="right"
                />
              </>
            )}
            {label === 'ROAS' && (
              <>
                <span
                  data-tip="Revenue over ad spend"
                  data-for="roas-tooltip"
                  className="m-x-1"
                >
                  <SVG src="/images/recommendations/icon-info.svg" />
                </span>
                <ReactTooltip
                  id="roas-tooltip"
                  type="dark"
                  effect="solid"
                  place="right"
                />
              </>
            )}

            {!hideSort && (
              <>
                {sort === value ? (
                  <>
                    {dir === 'desc' ? (
                      <SVG
                        src="/images/insights/caret-down.svg"
                        className="m-l-1"
                      />
                    ) : (
                      <SVG
                        src="/images/insights/caret-up.svg"
                        className="m-l-1"
                      />
                    )}
                  </>
                ) : (
                  <SVG src="/images/insights/caret.svg" className="m-l-1" />
                )}
              </>
            )}
          </span>
        </div>
      </th>
    )
  }
}

const TableItem = (props) => {
  const {
    row,
    networks,
    assignees,
    statuses,
    adsStats,
    sort,
    group,
    columns,
    draggedColumn,
    favorites,
    setFavorites,
    refreshFavorites,
    filters,
    setFilters,
    currencyCode,
    isSingleDate,
    hideFilter,
    tooltipLabel,
    isUnknownRow,
  } = props

  const { grp, name } = row
  const values = useMemo(
    () => calculateValues(row, currencyCode),
    [row, currencyCode]
  )
  const favorite = findFavorite(favorites, group, name, grp)

  const { showSnackbar } = useSnackbar()

  const displayName =
    name?.toLowerCase() === 'unknown'
      ? 'Conversion details not available'
      : name || ''

  return (
    <>
      <tr>
        <td
          className={varClass({
            'text-dark text-bold text-nowrap sticky left-0 background-white':
              true,
            'z-1': !isUnknownRow,
            'z-2': isUnknownRow,
          })}
          style={{ maxWidth: '380px' }}
        >
          <div className="first-column">
            <div className="name row row-center row-space-between row-nowrap vertical-middle gap-4">
              <span>
                <span
                  data-tip={
                    (displayName || grp).length > 35 ? displayName || grp : ''
                  }
                  data-for="table-tooltip"
                >
                  {truncate(
                    toUnicode(
                      displayName || grp || 'Conversion details not available'
                    ),
                    35
                  )}
                </span>
                {group === 'date' && isSingleDate && ':00'}
                {!!tooltipLabel && (
                  <>
                    <span
                      data-tip
                      data-for="statistic-tooltip"
                      className="m-l-1"
                    >
                      <SVG src="/images/recommendations/icon-info.svg" />
                    </span>
                    <ReactTooltip
                      id="statistic-tooltip"
                      type="dark"
                      effect="solid"
                      place="bottom"
                    >
                      {tooltipLabel}
                    </ReactTooltip>
                  </>
                )}
                {row.prev && (
                  <>
                    {['date', 'hour', 'week', 'month', 'year'].includes(
                      group
                    ) && (
                      <div className="text-smaller text-light o-60 m-t-1">
                        {row.prev.grp}
                        {group === 'date' && isSingleDate && ':00'}
                      </div>
                    )}
                  </>
                )}
              </span>
              {!hideFilter && (
                <div className="row row-narrow row-nowrap">
                  {group === 'name' && (
                    <>
                      {favorite ? (
                        <button
                          className="link link-filter text-new-orange"
                          onClick={async () => {
                            if (favorite.favoriteUuid) {
                              setFavorites(
                                favorites.filter(
                                  (fav) =>
                                    fav.favoriteUuid !== favorite.favoriteUuid
                                )
                              )
                              await deleteRequest(
                                '/insights/v1/favoritestatistics',
                                {
                                  favoriteUuid: favorite.favoriteUuid,
                                }
                              )
                              refreshFavorites()
                            }
                          }}
                        >
                          <SVG
                            src="/images/icon-star-filled.svg"
                            className="text-new-orange"
                          />
                        </button>
                      ) : (
                        <button
                          className="link link-filter"
                          onClick={async () => {
                            setFavorites([
                              ...favorites,
                              {
                                favoriteGroupBy: group,
                                favoriteGroupName: displayName || grp,
                              },
                            ])
                            await postRequest(
                              '/insights/v1/favoritestatistics',
                              {
                                favoriteGroupBy: group,
                                favoriteGroupName: displayName || grp,
                              }
                            )
                            refreshFavorites()
                          }}
                        >
                          <SVG src="/images/icon-star.svg" />
                        </button>
                      )}
                    </>
                  )}
                  {(group === 'page' || group === 'target') && (
                    <>
                      <button
                        className="link link-filter"
                        onClick={() => {
                          copyToClipboard(
                            toUnicode(
                              displayName ||
                                grp ||
                                'Conversion details not available'
                            )
                          )
                          showSnackbar('Copied to clipboard!')
                        }}
                      >
                        <SVG
                          src="/images/icon-copy.svg"
                          className="vertical-middle"
                        />
                      </button>
                      <a
                        href={`https://${toUnicode(
                          displayName ||
                            grp ||
                            'Conversion details not available'
                        )}`}
                        target="_blank"
                        className="link link-filter"
                      >
                        <SVG
                          src="/images/icon-outside-new.svg"
                          className="vertical-middle"
                        />
                      </a>
                    </>
                  )}
                  {(group === 'name' || group === 'boost') && (
                    <button
                      className="link link-filter"
                      onClick={async (event) => {
                        const response = await getRequest(
                          `/advs/v1/uuid/${row.name || row.grp}`
                        )
                        if (response.data) {
                          navigateTo(event, `/merchants/${response.data}`)
                        }
                      }}
                    >
                      <SVG
                        src="/images/icon-details.svg"
                        className="vertical-middle"
                      />
                    </button>
                  )}
                  <button
                    className="link link-filter"
                    onClick={(event) => {
                      const rawValue = grp || displayName || ''
                      const rawLabel =
                        displayName ||
                        grp ||
                        'Tracking details not available to Heylink'

                      const key = groupFilterKeys[group] || group
                      const value =
                        key === 'date' || key === 'month' || key === 'year'
                          ? rawValue.split(' ')[0]
                          : rawValue
                      const label =
                        key === 'date' || key === 'month' || key === 'year'
                          ? rawLabel.split(' ')[0]
                          : rawLabel

                      const newFilters = [...filters]
                      const existingFilter = newFilters.find(
                        (filter) => filter.key === key
                      )
                      if (existingFilter) {
                        existingFilter.key = key
                        existingFilter.value = value
                        existingFilter.label = label
                      } else {
                        newFilters.push({ key, value, label })
                      }

                      if (
                        event.ctrlKey ||
                        event.metaKey ||
                        event.button === 2
                      ) {
                        const queryGroup = `tab=${group}`
                        const queryFilters = `filters=${lz.compressToEncodedURIComponent(
                          JSON.stringify(newFilters)
                        )}`
                        const newUrl =
                          window.location.pathname +
                          `?${queryGroup}&${queryFilters}`
                        navigateTo(event, newUrl)
                      } else {
                        setFilters(newFilters)
                      }
                    }}
                  >
                    <SVG
                      src="/images/icon-filter.svg"
                      className="vertical-middle"
                    />
                  </button>
                </div>
              )}
            </div>
          </div>
        </td>

        {columns.map((column, index) =>
          index === 0 ? null : (
            <td
              key={column}
              className={varClass({
                'text-right': true,
                'sort-highlight': sort === column,
                'drag-highlight': draggedColumn === column,
                'background-white text-nowrap': isUnknownRow,
              })}
            >
              <TableValue
                row={displayName || grp}
                {...{
                  rawValues: row,
                  values,
                  column,
                  networks,
                  assignees,
                  statuses,
                  adsStats,
                }}
              />
            </td>
          )
        )}
      </tr>
      {(group === 'page' || group === 'target') && (
        <ReactTooltip
          id={displayName || grp}
          type="dark"
          effect="solid"
          place="bottom"
        >
          {toUnicode(displayName || grp || 'Conversion details not available')}
        </ReactTooltip>
      )}
      {group === 'page' && (
        <ReactTooltip
          id={displayName || grp}
          type="dark"
          effect="solid"
          place="bottom"
        >
          {toUnicode(displayName || grp || 'Conversion details not available')}
        </ReactTooltip>
      )}
    </>
  )
}

export const DragTable = (props) => {
  const { shownRows } = props

  const { item, currentOffset, isDragging } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }))

  if (!isDragging || !currentOffset) {
    return null
  }

  const columns = [item?.value]

  return (
    <div
      className="insights-statistics-card drag-card"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        transform: `translate(${currentOffset?.x}px, ${currentOffset?.y}px)`,
        pointerEvents: 'none',
        zIndex: 100,
      }}
    >
      <table
        className="table table-bordered table-sortable insights-statistics-table drag-table relative"
        style={{
          position: 'relative',
          width: 100,
          height: 100,
        }}
      >
        <thead className="text-nowrap">
          <tr>
            <th className="relative vertical-middle p-l-2 p-r-4 drag-highlight">
              <div className="row row-fill row-nowrap">
                <span className="cursor-grab text-blue m-r-10">
                  <SVG
                    src="/images/insights/icon-drag.svg"
                    className="handle"
                  />
                </span>
                <span className="column-label">
                  {tableLabels[item?.value]}
                  <SVG src="/images/insights/caret.svg" className="m-l-1" />
                </span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody className="text-nowrap">
          {map(shownRows, (row, index) => (
            <DragTableItem key={row.grp || row.name || index} />
          ))}
          {isBlank(shownRows) && (
            <tr>
              <td colSpan={10} className="text-center text-light text-bolder">
                Nothing found
              </td>
            </tr>
          )}
          <tr className="blank-space">
            <td colSpan={10} />
          </tr>
        </tbody>
        <tfoot>
          <tr className="text-nowrap">
            {columns.map((column) => (
              <th key={column} className="text-right" />
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

const DragTableItem = () => {
  return (
    <tr>
      <td className="text-right" />
    </tr>
  )
}
